import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { couponService } from '../services/couponService';

export default function RedeemCouponModal({ open, onClose, onSuccess, onNavigate }) {
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successResult, setSuccessResult] = useState(null);

  const handleClose = () => {
    if (loading) return;
    setCouponCode('');
    setError(null);
    setSuccessResult(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const trimmed = couponCode.trim().toUpperCase();
    if (!trimmed) {
      setError('Please enter a valid promotional coupon code.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessResult(null);

    try {
      const response = await couponService.redeemPromotionalCoupon(trimmed);
      if (response && response.success === false) {
        throw {
          status: 400,
          message:
            response.message ||
            'Promotional coupon has already been redeemed or is invalid.',
          data: response,
        };
      }

      setSuccessResult({
        message: response?.message || 'Promotional coupon redeemed successfully.',
        data: response?.data || response,
      });

      if (onSuccess) {
        onSuccess(response);
      }
    } catch (err) {
      console.error('Promotional coupon redeem failed:', err);
      const serverMessage =
        err?.message ||
        (typeof err?.data === 'string' && err.data) ||
        err?.data?.message ||
        err?.data?.error ||
        err?.data?.errorMessage ||
        'Promotional coupon has already been redeemed or is invalid.';
      setError(serverMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetForAnother = () => {
    setCouponCode('');
    setError(null);
    setSuccessResult(null);
  };

  // Extract display values from successResult
  const successMessage =
    successResult?.message ||
    successResult?.data?.message ||
    'Promotional coupon redeemed successfully.';
  const resultData = successResult?.data || successResult || {};
  const cashbackPaise =
    resultData?.cashbackAmountPaise ??
    resultData?.amountPaise ??
    (resultData?.amount != null ? resultData.amount * 100 : 0);
  const amountInRupees = (cashbackPaise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const couponCodeDisplay = resultData?.couponCode || couponCode;
  const tier = resultData?.tier || 'PROMOTIONAL';
  const outcomeType = resultData?.outcomeType;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#153A35',
          color: '#F5F1E6',
          borderRadius: '24px',
          border: '1px solid rgba(245, 241, 230, 0.14)',
          p: { xs: 1, sm: 1.5 },
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              backgroundColor: 'rgba(212, 162, 76, 0.15)',
              border: '1px solid rgba(212, 162, 76, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#D4A24C',
            }}
          >
            <CardGiftcardIcon />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: '"Fraunces", serif',
                fontSize: '20px',
                fontWeight: 700,
                color: '#F5F1E6',
                lineHeight: 1.2,
              }}
            >
              Redeem Coupon
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                color: 'rgba(245, 241, 230, 0.6)',
                fontWeight: 400,
              }}
            >
              Enter promotional voucher code
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: 'rgba(245, 241, 230, 0.6)',
            '&:hover': {
              color: '#F5F1E6',
              backgroundColor: 'rgba(245, 241, 230, 0.08)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5, pb: 2 }}>
        {successResult ? (
          <Box sx={{ py: 0.5 }}>
            {/* Success Card */}
            <Box
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: '18px',
                backgroundColor: 'rgba(47, 143, 118, 0.12)',
                border: '1px solid rgba(47, 143, 118, 0.32)',
                textAlign: 'center',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.06)',
              }}
            >
              {/* Checkmark Icon */}
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(47, 143, 118, 0.22)',
                  border: '1px solid rgba(47, 143, 118, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                  boxShadow: '0 0 24px rgba(47, 143, 118, 0.3)',
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 34, color: '#2F8F76' }} />
              </Box>

              {/* Message from Response */}
              <Typography
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#F5F1E6',
                  mb: 0.5,
                  lineHeight: 1.3,
                }}
              >
                {successMessage}
              </Typography>

              <Typography sx={{ fontSize: '12.5px', color: 'rgba(245, 241, 230, 0.65)', mb: 2 }}>
                Your reward has been credited directly to your iSoret wallet.
              </Typography>

              {/* Prominent Amount Box */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, rgba(212, 162, 76, 0.18) 0%, rgba(15, 46, 43, 0.85) 100%)',
                  border: '1px solid rgba(212, 162, 76, 0.35)',
                  boxShadow: '0 8px 18px rgba(0, 0, 0, 0.25)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8, mb: 0.5 }}>
                  <AccountBalanceWalletIcon sx={{ fontSize: 16, color: '#D4A24C' }} />
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#D4A24C',
                    }}
                  >
                    Cashback Amount
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: '"Fraunces", serif',
                    fontSize: { xs: '32px', sm: '36px' },
                    fontWeight: 800,
                    color: '#E8C078',
                    lineHeight: 1.1,
                  }}
                >
                  ₹{amountInRupees}
                </Typography>
              </Box>

              {/* Summary Details Chips */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  mt: 2,
                  pt: 1.5,
                  borderTop: '1px dashed rgba(245, 241, 230, 0.12)',
                }}
              >
                {couponCodeDisplay && (
                  <Chip
                    icon={<ConfirmationNumberIcon sx={{ fontSize: '14px !important', color: '#D4A24C !important' }} />}
                    label={couponCodeDisplay}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(212, 162, 76, 0.12)',
                      color: '#F5F1E6',
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 700,
                      fontSize: '11.5px',
                      border: '1px solid rgba(212, 162, 76, 0.25)',
                    }}
                  />
                )}
                {tier && (
                  <Chip
                    label={tier}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(47, 143, 118, 0.18)',
                      color: '#2F8F76',
                      fontWeight: 700,
                      fontSize: '11px',
                      letterSpacing: '0.04em',
                      border: '1px solid rgba(47, 143, 118, 0.28)',
                    }}
                  />
                )}
                {outcomeType && (
                  <Chip
                    label={outcomeType.replace(/_/g, ' ')}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(245, 241, 230, 0.08)',
                      color: 'rgba(245, 241, 230, 0.75)',
                      fontSize: '11px',
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: '13px', color: 'rgba(245, 241, 230, 0.75)', mb: 1.5, lineHeight: 1.5 }}>
              Have a promotional voucher or partner code? Enter it below to claim your benefits.
            </Typography>

            <TextField
              fullWidth
              autoFocus
              placeholder="e.g. FSDH3453, FESTIVE100"
              value={couponCode}
              onChange={(e) => {
                setCouponCode(e.target.value.toUpperCase());
                if (error) setError(null);
              }}
              disabled={loading}
              error={Boolean(error)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <ConfirmationNumberIcon sx={{ color: error ? '#E4572E' : '#D4A24C', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(15, 46, 43, 0.6)',
                  color: '#F5F1E6',
                  fontFamily: '"JetBrains Mono", monospace',
                  letterSpacing: '0.08em',
                  fontWeight: 600,
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: error ? '#E4572E' : 'rgba(245, 241, 230, 0.16)',
                  },
                  '&:hover fieldset': {
                    borderColor: error ? '#E4572E' : 'rgba(212, 162, 76, 0.4)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: error ? '#E4572E' : '#D4A24C',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(245, 241, 230, 0.4)',
                  opacity: 1,
                  fontFamily: 'inherit',
                  letterSpacing: 'normal',
                },
              }}
            />

            {error && (
              <Alert
                severity="error"
                sx={{
                  backgroundColor: 'rgba(228, 87, 46, 0.15)',
                  color: '#FFA28A',
                  border: '1px solid rgba(228, 87, 46, 0.3)',
                  borderRadius: '12px',
                  fontSize: '13px',
                  mb: 1.5,
                  wordBreak: 'break-word',
                  '& .MuiAlert-icon': {
                    color: '#E4572E',
                  },
                }}
              >
                {error}
              </Alert>
            )}

            <Typography sx={{ fontSize: '11px', color: 'rgba(245, 241, 230, 0.45)', fontStyle: 'italic' }}>
              * Promotional codes are single-use and subject to campaign terms.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
        {successResult ? (
          <>

            {onNavigate && (
              <Button
                variant="outlined"
                onClick={() => {
                  handleClose();
                  onNavigate('wallet');
                }}
                sx={{
                  borderRadius: '12px',
                  borderColor: 'rgba(212, 162, 76, 0.35)',
                  color: '#D4A24C',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: 2,
                  '&:hover': {
                    borderColor: '#D4A24C',
                    backgroundColor: 'rgba(212, 162, 76, 0.08)',
                  },
                }}
              >
                View Wallet
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                borderRadius: '12px',
                backgroundColor: '#D4A24C',
                color: '#0F2E2B',
                fontWeight: 700,
                textTransform: 'none',
                px: 3,
                '&:hover': {
                  backgroundColor: '#E8C078',
                },
              }}
            >
              Done
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleClose}
              disabled={loading}
              sx={{
                borderRadius: '12px',
                color: 'rgba(245, 241, 230, 0.7)',
                fontWeight: 600,
                textTransform: 'none',
                px: 2,
                '&:hover': {
                  color: '#F5F1E6',
                  backgroundColor: 'rgba(245, 241, 230, 0.05)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || !couponCode.trim()}
              startIcon={loading && <CircularProgress size={16} sx={{ color: '#0F2E2B' }} />}
              sx={{
                borderRadius: '12px',
                backgroundColor: '#D4A24C',
                color: '#0F2E2B',
                fontWeight: 700,
                textTransform: 'none',
                px: 3,
                py: 0.9,
                '&:hover': {
                  backgroundColor: '#E8C078',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(212, 162, 76, 0.3)',
                  color: 'rgba(15, 46, 43, 0.6)',
                },
              }}
            >
              {loading ? 'Redeeming...' : 'Redeem Code'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
